import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, FileText, ShoppingBag, Truck } from "lucide-react";

const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function CAReport() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/ca/gst-register", { params: { month } }).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [month]);

  return (
    <div className="space-y-5" data-testid="ca-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">CA Report — GST Register</h1>
          <p className="text-sm text-slate-500">Month-wise saare bills (POS + website) GST breakup ke saath + purchases. Read-only.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-44 bg-white" data-testid="ca-month-input" />
          <Button variant="outline" data-testid="ca-export-btn" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/invoices`, "_blank")}>
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
        </div>
      </div>

      {!data ? (
        <Card className="p-10 text-center text-slate-400 text-sm">Loading…</Card>
      ) : data.error ? (
        <Card className="p-10 text-center text-red-500 text-sm">Report load nahi ho payi.</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500">Total Bills</div><div className="font-mono-num text-2xl font-extrabold text-slate-900" data-testid="ca-total-bills">{data.sales_totals.count}</div></Card>
            <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500">Taxable Sales</div><div className="font-mono-num text-2xl font-extrabold text-slate-900" data-testid="ca-taxable">{fmt(data.sales_totals.taxable)}</div></Card>
            <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500">GST Collected</div><div className="font-mono-num text-2xl font-extrabold text-emerald-800" data-testid="ca-gst">{fmt(data.sales_totals.gst)}</div></Card>
            <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500">Grand Total</div><div className="font-mono-num text-2xl font-extrabold text-emerald-900" data-testid="ca-grand">{fmt(data.sales_totals.grand)}</div></Card>
          </div>

          <Card className="border-slate-200">
            <div className="p-4 border-b border-slate-100 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-emerald-700" />
              <h2 className="font-display font-bold text-slate-900">Sales (GST Invoices) — {data.month}</h2>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Taxable</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.sales.map((s) => (
                    <TableRow key={s.invoice_no} data-testid={`ca-sale-${s.invoice_no}`}>
                      <TableCell className="font-mono-num font-bold">{s.invoice_no}</TableCell>
                      <TableCell className="text-sm text-slate-500">{s.date}</TableCell>
                      <TableCell className="text-sm">{s.customer_name}</TableCell>
                      <TableCell><Badge variant="outline" className="uppercase text-[10px]">{s.source}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className="uppercase text-[10px]">{s.payment_mode}</Badge></TableCell>
                      <TableCell className="text-right font-mono-num">{fmt(s.taxable)}</TableCell>
                      <TableCell className="text-right font-mono-num">{fmt(s.gst)}</TableCell>
                      <TableCell className="text-right font-mono-num font-bold text-emerald-900">{fmt(s.grand_total)}</TableCell>
                    </TableRow>
                  ))}
                  {data.sales.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Is month mein koi bill nahi</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card className="border-slate-200">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-amber-700" />
                <h2 className="font-display font-bold text-slate-900">Purchases — {data.month}</h2>
              </div>
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="ca-purchase-total">{data.purchase_totals.count} POs • {fmt(data.purchase_totals.amount)}</Badge>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.purchases.map((p, i) => (
                    <TableRow key={i} data-testid={`ca-po-${i}`}>
                      <TableCell className="font-mono-num font-bold">{p.po_no || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-500">{p.date}</TableCell>
                      <TableCell className="text-sm">{p.supplier}</TableCell>
                      <TableCell><Badge variant="outline" className="uppercase text-[10px]">{p.status}</Badge></TableCell>
                      <TableCell className="text-right font-mono-num font-bold">{fmt(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {data.purchases.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Is month mein koi purchase nahi</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card className="p-4 border-slate-200 flex items-center gap-3 text-xs text-slate-500">
            <FileText className="w-4 h-4 text-emerald-700 shrink-0" />
            Month badalne ke liye upar month choose karein. Excel export mein poore invoices ki detail aa jayegi.
          </Card>
        </>
      )}
    </div>
  );
}
